"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Expertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tiers = [{
    label: "CONSTRUCTION",
    scale: "Full-Service",
    description: "From ground-up builds to complex renovations, we deliver structural integrity and architectural precision for every project.",
    href: "/BuildingExcellencePage"
  }, {
    label: "MANAGEMENT",
    scale: "Proactive",
    description: "Comprehensive property stewardship ensuring your investment remains in peak condition through diligent oversight and maintenance.",
    href: "/PropertyStewardshipPage"
  }];
  return <section data-section-id="2143" id="our-dual-expertise" className="relative overflow-hidden bg-background text-foreground py-32 md:py-44 light">
      {}
      <div className="absolute top-0 left-0 right-0 h-px bg-border" />

      <div className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-[4.5rem]">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute left-4 md:left-10 lg:left-[4.5rem] top-24 md:top-32 select-none font-serif font-extralight leading-none text-foreground/[0.04]" style={{
        fontSize: "clamp(12rem, 22vw, 22rem)"
      }}>
          01
        </div>

        <div ref={ref} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start transition-all duration-[640ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col">
            {}
            <div className="flex items-center gap-4 mb-10">
              <span className="block h-px w-6 bg-border" />
              <span className="font-mono uppercase text-muted-foreground" style={{
              fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
              letterSpacing: "0.18em",
              fontWeight: 500
            }}>Expertise</span>
            </div>

            {}
            <h1 className="font-default text-foreground mb-8" style={{
            fontSize: "clamp(2.75rem, 6.5vw, 6.25rem)",
            fontWeight: 400,
            lineHeight: 0.96,
            letterSpacing: "-0.025em"
          }}>
              Building legacies,{" "}
              <span className="relative inline-block">
                <em className="font-default italic text-foreground" style={{
                fontVariationSettings: '"opsz" 12'
              }}>
                  managing
                </em>
                <span className="absolute -bottom-1 left-0 flex items-center w-full">
                  <span className="block h-px flex-1 bg-border" />
                  <span className="block h-1.5 w-1.5 rounded-lg bg-secondary ml-0" />
                </span>
              </span>{" "}
              your vision.
            </h1>

            {}
            <p className="text-muted-foreground mb-12 max-w-[42ch]" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
            lineHeight: 1.6,
            fontWeight: 400
          }}>
              Moscariello Construction Co, Inc provides expert craftsmanship and dedicated property management. 
              We bridge the gap between high-end construction and long-term asset stewardship.
            </p>

            {}
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-0 mb-12">
              {}
              <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-border" />
              {tiers.map((tier, i) => <div key={tier.label} data-index={i} className={`flex flex-col ${i === 0 ? "md:pr-10" : "md:pl-10"} ${i === 1 ? "md:border-0 border-t md:border-t-0 border-border pt-10 md:pt-0" : ""}`}>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="font-mono uppercase text-foreground" style={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.18em",
                  fontWeight: 500
                }}>
                      {tier.label}
                    </span>
                    <span className="font-mono text-muted-foreground" style={{
                  fontSize: "0.6875rem",
                  letterSpacing: "0.08em"
                }}>
                      — {tier.scale}
                    </span>
                  </div>
                  <p className="text-muted-foreground mb-6" style={{
                fontSize: "0.9375rem",
                lineHeight: 1.65
              }}>
                    {tier.description}
                  </p>
                  <Link to={tier.href} className="group inline-flex items-center gap-2 self-start font-mono uppercase text-foreground" style={{
                fontSize: "0.8125rem",
                letterSpacing: "0.04em",
                fontWeight: 500
              }}>
                    <span className="relative">
                      Learn More
                      <span className="absolute left-0 -bottom-1 h-px w-3 bg-foreground transition-all duration-[280ms] ease-out group-hover:w-full" />
                      <span className="absolute -bottom-1 left-3 group-hover:left-full h-px w-1 bg-secondary transition-all duration-[280ms] ease-out" />
                    </span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>)}
            </div>

            {}
            <div>
              <Button asChild className="rounded-lg px-8 py-4 h-auto bg-secondary hover:bg-secondary text-secondary-foreground font-default font-medium uppercase transition-all duration-[220ms] ease-out hover:-translate-y-0.5 hover:shadow-lg" style={{
              fontSize: "0.8125rem",
              letterSpacing: "0.04em",
              fontWeight: 500
            }}>
                <Link to="/ConnectWithUsPage">Request Consultation</Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <div className="relative w-full" style={{
            aspectRatio: "4 / 5"
          }}>
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/moscariello_construction_site.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/moscariello_construction_site.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/moscariello_construction_site.webp-wvc-sizes" alt="Americo Moscariello overseeing a high-end construction site in Collegeville" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

              {}
              <span aria-hidden="true" className="absolute top-0 left-0 w-2 h-2 border-t border-l border-muted-foreground" />
              <span aria-hidden="true" className="absolute top-0 right-0 w-2 h-2 border-t border-r border-muted-foreground" />
              <span aria-hidden="true" className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-muted-foreground" />
              <span aria-hidden="true" className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-muted-foreground" />

              {}
              <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 flex items-center gap-3">
                <span className="block h-px w-6 bg-secondary" />
                <span className="font-mono uppercase text-foreground" style={{
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                fontWeight: 400
              }}>
                  Project Site / Collegeville, PA / Quality Assured
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
    </section>;
}